import React, { useState, useCallback, memo, useEffect } from 'react';
import PropTypes from 'prop-types';

import styles from './WizardForm.module.scss';
import cx from 'classnames';

import {
  Form,
  Modal,
  ProgressIndicator,
  ProgressStep,
  Loading,
} from '@carbon/react';

const WizardStep = memo(({ children }) => {
  return <>{children}</>;
});

WizardStep.propTypes = {
  children: PropTypes.node,
  label: PropTypes.string,

  description: PropTypes.string,
};

const WizardForm = memo(
  ({
    label,
    max_steps,
    steps,
    onChangeStep = () => {},
    onBeforeChangeStep,
    onAbort,
    onSubmit,
    isLoading,
  }) => {
    const [current_step, setStep] = useState(1);

    const handleChangeStep = useCallback(
      (new_step) => {
        const old_step = current_step;
        setStep(new_step);
        onChangeStep(new_step, old_step);
      },
      [setStep, current_step, onChangeStep]
    );

    useEffect(() => {
      setStep(1);
    }, []);

    useEffect(() => {
      setStep(1);
    }, [max_steps]);

    useEffect(() => {
      steps.forEach((step, i) => {
        if (Object.prototype.hasOwnProperty.call(step.props,'validation')) {
          if (
            Object.prototype.hasOwnProperty.call(step.props,'validation_error') &&
            step.props.validation_error === true
          ) {
            setStep(i + 1);
            return;
          }
        }
      });
    }, [steps]);

    const handleAbort = (e) => {
      const { currentTarget: button } = e;

      if (button && button.className === 'cds--modal-close') {
        onAbort();
        return;
      }

      if (current_step > 1) {
        onBeforeChangeStep(current_step - 1, current_step);
        handleChangeStep(current_step - 1);
      } else {
        onAbort();
      }
    };

    const handleSubmit = () => {
      if (current_step !== max_steps) {
        if (onBeforeChangeStep(current_step + 1, current_step)) {
          nextStep();
        }
      } else {
        nextStep();
      }
    };

    const nextStep = () => {
      if (current_step === max_steps) {
        onSubmit();
      } else {
        handleChangeStep(current_step + 1);
      }
    };

    return (
      <Modal
        open={true}
        danger={false}
        primaryButtonText={current_step === max_steps ? 'Anlegen' : 'Weiter'}
        secondaryButtonText={current_step > 1 ? 'Zurück' : 'Abrechen'}
        modalHeading={label}
        onRequestClose={handleAbort}
        onRequestSubmit={handleSubmit}
        className={cx(styles.Container, "overflowVisible")}>
        <ProgressIndicator currentIndex={current_step - 1}>
          {steps.map((step, i) => {
            return (
              <ProgressStep
                key={`step_${i}_pr`}
                className={cx(styles.ProgressStepItem)}
                label={step.props.label}
                description={step.props.description}
                invalid={
                  Object.prototype.hasOwnProperty.call(step.props,'validation')
                    ? step.props.validation === false
                    : false
                }
              />
            );
          })}
        </ProgressIndicator>
        <br />

        <Form>{steps[current_step - 1].props.children}</Form>

        <Loading active={isLoading} withOverlay={true} />
      </Modal>
    );
  }
);

WizardForm.propTypes = {
  label: PropTypes.string,
  max_steps: PropTypes.number,
  steps: PropTypes.array,
  onChangeStep: PropTypes.func,
  onAbort: PropTypes.func,
  onSubmit: PropTypes.func,
  isLoading: PropTypes.bool,
};

export { WizardForm, WizardStep };
